import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { REVIEWS, REVIEW_PHOTOS } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";

const ReviewCard = ({ quote, name, role, testid }) => (
  <article
    data-testid={testid}
    className="snap-start shrink-0 w-[82vw] sm:w-[440px] lg:w-[480px] rounded-2xl border border-[#07412b]/12 bg-[#fbf8f0] p-7 sm:p-8 shadow-[0_20px_40px_-30px_rgba(7,65,43,0.4)]"
  >
    <Quote className="text-[#ddb128]" size={22} />
    <p className="font-body font-light text-[#07412b] text-[15px] sm:text-base leading-relaxed mt-4">
      "{quote}"
    </p>
    <div className="mt-6 flex items-center gap-3">
      <span className="inline-block h-8 w-px bg-[#ddb128]" />
      <div>
        <p className="font-display text-[#07412b] text-lg leading-tight">
          {name}
        </p>
        <p className="font-body text-[#c56928] text-[11px] tracking-[0.22em] uppercase mt-1">
          {role}
        </p>
      </div>
    </div>
  </article>
);

export const Reviews = () => {
  const railRef = useRef(null);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const step = Math.min(500, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-[#f0e9d6] py-12 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <FadeUp>
              <span className="font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
                Guest Book
              </span>
            </FadeUp>
            <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] leading-[1] mt-2">
              <MaskLineInView>You come as a guest.</MaskLineInView>
              <MaskLineInView delay={0.08}>You leave as family.</MaskLineInView>
            </h2>
            <FadeUp delay={0.15}>
              <p className="font-body font-light text-[#07412b]/70 text-base sm:text-lg mt-4 max-w-lg leading-relaxed">
                A few kind words from the writers, doctors, filmmakers, lawyers
                and dreamers who've spent time with us at The Brook.
              </p>
            </FadeUp>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="reviews-prev"
              aria-label="Previous review"
              className="h-11 w-11 rounded-full border border-[#07412b]/20 flex items-center justify-center hover:bg-[#07412b] hover:text-[#ddb128] transition-colors text-[#07412b]"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="reviews-next"
              aria-label="Next review"
              className="h-11 w-11 rounded-full border border-[#07412b]/20 flex items-center justify-center hover:bg-[#07412b] hover:text-[#ddb128] transition-colors text-[#07412b]"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Guest photo ribbon — auto-scrolling wall of memories */}
      <div className="mt-10 lg:mt-12 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
        <div className="flex w-max animate-marquee-l hover:[animation-play-state:paused]">
          {[0, 1].map((group) => (
            <div key={group} className="flex items-center gap-4 sm:gap-5 pr-4 sm:pr-5" aria-hidden={group === 1}>
              {REVIEW_PHOTOS.map((p, i) => (
                <motion.figure
                  key={`${group}-${i}`}
                  data-testid={group === 0 ? `guest-photo-${i + 1}` : undefined}
                  className="relative shrink-0 w-[280px] sm:w-[360px] lg:w-[420px] aspect-[4/3] overflow-hidden rounded-2xl shadow-[0_18px_40px_-24px_rgba(7,65,43,0.35)]"
                >
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#07412b]/70 to-transparent p-3 sm:p-4">
                    <p className="font-script text-[#ddb128] text-base sm:text-lg leading-tight">
                      {p.alt}
                    </p>
                  </div>
                </motion.figure>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div
        ref={railRef}
        className="mt-12 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-lenis-prevent
      >
        <div className="flex gap-5 sm:gap-6 px-5 lg:px-8 max-w-7xl mx-auto pb-2">
          {REVIEWS.map((r, i) => (
            <ReviewCard
              key={i}
              testid={`review-${i + 1}`}
              quote={r.quote}
              name={r.name}
              role={r.role}
            />
          ))}
          <div className="shrink-0 w-2" aria-hidden="true" />
        </div>
      </div>

      <p className="mt-8 text-center font-body text-[#07412b]/55 text-sm tracking-wide">
        Swipe to read more →
      </p>
    </section>
  );
};
